import type { ReactNode } from "react";
import "./data-table.style.scss";

export interface DataTableColumn<T> {
	key: string;
	label: string;
	render: (row: T) => ReactNode;
	className?: string;
	align?: "left" | "right";
}

interface DataTableProps<T> {
	columns: DataTableColumn<T>[];
	data: T[];
	idKey: keyof T & string;
	onRowClick?: (row: T) => void;
	selectedRowId?: string;
	emptyState?: ReactNode;
	className?: string;
}

// Generic, SCSS-styled table (columns config + idKey + onRowClick, no row
// selection/bulk-actions/row-actions menu — none of bill-list's table view
// needs those, and this app hard-blocks below 680px so there's no mobile
// stacked-cell fallback to build either).
function DataTable<T>({
	columns,
	data,
	idKey,
	onRowClick,
	selectedRowId,
	emptyState,
	className,
}: DataTableProps<T>) {
	function renderRow(row: T) {
		const rowId = String(row[idKey]);
		return (
			<tr
				key={rowId}
				className={`data-table-row ${selectedRowId === rowId ? "selected" : ""}`}
				onClick={() => onRowClick?.(row)}
			>
				{columns.map((col) => (
					<td
						key={col.key}
						className={`${col.align === "right" ? "align-right" : ""} ${col.className ?? ""}`}
					>
						{col.render(row)}
					</td>
				))}
			</tr>
		);
	}

	return (
		<div className={`data-table-wrapper ${className ?? ""}`}>
			<table className="data-table">
				<thead>
					<tr>
						{columns.map((col) => (
							<th key={col.key} className={col.align === "right" ? "align-right" : ""}>
								{col.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{data.length === 0
						? emptyState && (
								<tr>
									<td colSpan={columns.length} className="data-table-empty">
										{emptyState}
									</td>
								</tr>
							)
						: data.map(renderRow)}
				</tbody>
			</table>
		</div>
	);
}

export default DataTable;
